import React, { useState } from 'react'
import { postcodeService, Address } from '@/services/postcode'
import { Button } from './Button'

interface PostcodeLookupProps {
  onAddressSelect: (address: Address) => void
  initialPostcode?: string
  label?: string
  error?: string
}

export const PostcodeLookup: React.FC<PostcodeLookupProps> = ({
  onAddressSelect,
  initialPostcode = '',
  label = 'Postcode',
}) => {
  const [postcode, setPostcode] = useState(initialPostcode)
  const [addresses, setAddresses] = useState<Address[]>([])
  const [selectedAddress, setSelectedAddress] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [showDropdown, setShowDropdown] = useState(false)

  const handleLookup = async () => {
    if (!postcode.trim()) {
      setLookupError('Please enter a postcode')
      return
    }

    setIsLoading(true)
    setLookupError(null)
    setAddresses([])

    try {
      const response = await postcodeService.lookup(postcode)

      if (response.success && response.addresses) {
        setAddresses(response.addresses)
        setShowDropdown(true)
        if (response.addresses.length === 0) {
          setLookupError('No addresses found for this postcode')
        }
      } else {
        setLookupError(response.message || 'Failed to lookup postcode')
      }
    } catch (err: any) {
      setLookupError(err.response?.data?.detail || 'Failed to lookup postcode')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectAddress = (index: number) => {
    setSelectedAddress(index)
    const address = addresses[index]
    onAddressSelect(address)
    setShowDropdown(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleLookup()
    }
  }

  return (
    <div className="space-y-2">
      {/* Label above the row */}
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {/* Input + button on the same baseline */}
      <div className="flex gap-2">
        <input
          type="text"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="e.g., SW1A 1AA"
          className={`w-36 flex-shrink-0 px-3 text-sm border rounded-lg h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${
            lookupError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
          }`}
        />
        <Button
          type="button"
          onClick={handleLookup}
          isLoading={isLoading}
          disabled={!postcode.trim()}
          className="whitespace-nowrap flex-shrink-0"
        >
          Find Address
        </Button>
      </div>

      {lookupError && <p className="text-sm text-red-600">{lookupError}</p>}

      {showDropdown && addresses.length > 0 && (
        <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto bg-white shadow-lg">
          <div className="p-2 bg-gray-50 border-b text-sm text-gray-600 font-medium">
            {addresses.length} address{addresses.length !== 1 ? 'es' : ''} found - Select one:
          </div>
          {addresses.map((address, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectAddress(index)}
              className={`w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b last:border-b-0 ${
                selectedAddress === index ? 'bg-primary-100' : ''
              }`}
            >
              <div className="text-sm font-medium text-gray-900">{address.line_1}</div>
              {address.line_2 && (
                <div className="text-sm text-gray-600">{address.line_2}</div>
              )}
              <div className="text-sm text-gray-600">
                {address.post_town}, {address.postcode}
              </div>
            </button>
          ))}
        </div>
      )}

      {addresses.length > 0 && (
        <button
          type="button"
          onClick={() => {
            setAddresses([])
            setShowDropdown(false)
            setSelectedAddress(null)
          }}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          Enter address manually instead
        </button>
      )}
    </div>
  )
}
